/**
 * The Roster is the Load Balance Board. The classic week/day grids it replaced
 * were unmounted once no control could reach them any more.
 *
 * This file is the composition: the calendar state, the hooks that own the
 * data and the two server actions (Allocate, Publish), and the board.
 */
import { Box } from "@mui/material";
import { addDays, format, parseISO, startOfDay, startOfWeek } from "date-fns";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import useViewport from "../../../utils/use-viewport";
import { planner } from "../planner-ui/tokens";
import StagedAllocationDialog from "./components/staged-allocation-dialog";
import { useAllocation } from "./controller/use-allocation";
import { usePublishDrafts } from "./controller/use-publish-drafts";
import { useRosterData } from "./controller/use-roster-data";
import LoadBalanceBoard from "./load-balance-board";
import RosterSnackbar from "./ui/roster-snackbar";

const Roster: React.FC<{ layout?: "timeline" | "crew" }> = ({ layout = "timeline" }) => {
	const navigate = useNavigate();
	const { height, width } = useViewport();
	const boxRef = useRef<HTMLDivElement>(null);
	const [schedulerContainerHeight, setSchedulerContainerHeight] = useState(0);
	const [weekStartDate, setWeekStartDate] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
	const [selectedDate, setSelectedDate] = useState(new Date());

	// The board's move-target cache is graded against the rows on screen; any
	// reload that bypasses the board's own `changed()` (Allocate, the initial
	// fetch) calls this so a stale grade is never shown on the next drag.
	const invalidateTargetsRef = useRef<(() => void) | null>(null);
	const invalidateTargets = useCallback(() => invalidateTargetsRef.current?.(), []);

	const roster = useRosterData({ weekStartDate, selectedDate, onWeekLoaded: invalidateTargets });
	const allocation = useAllocation({ locationId: roster.selectedLocation, onAllocated: roster.fetchWeek });
	// The allocation dialog names the day or week it ran, and counts that span's orders.
	const allocationSpan = useMemo(() => {
		const span = allocation.span ?? { start: selectedDate, days: 1 };
		const from = startOfDay(span.start);
		const to = addDays(from, span.days);
		return {
			label: span.days === 1 ? format(from, "EEEE, MMMM d, yyyy") : `Week of ${format(from, "MMMM d, yyyy")}`,
			orderCount: roster.manufacturingOrders.filter((mo) => {
				const start = parseISO(mo.startTime);
				return start >= from && start < to;
			}).length,
		};
	}, [allocation.span, selectedDate, roster.manufacturingOrders]);
	const publish = usePublishDrafts({
		workcenters: roster.workcenters,
		currentUserId: roster.currentUserId,
		onPublished: roster.fetchWeek,
	});

	useEffect(() => {
		if (boxRef.current) {
			try {
				setSchedulerContainerHeight(height - boxRef.current.offsetTop);
			} catch (error) {
				// Do nothing
			}
		}
	}, [height, width]);

	const handleAllocate = useCallback(() => void allocation.allocateDay(selectedDate), [allocation, selectedDate]);
	const handleAllocateWeek = useCallback(() => allocation.allocateWeek(weekStartDate), [allocation, weekStartDate]);

	// Keep the week in step with the day the board shows.
	const handleDayDateChange = useCallback(
		(date: Date) => {
			setSelectedDate(date);
			const newWeekStart = startOfWeek(date, { weekStartsOn: 1 });
			setWeekStartDate((current) => (newWeekStart.getTime() !== current.getTime() ? newWeekStart : current));
		},
		[]
	);

	const openFlexScheduler = useCallback(() => navigate("/flex-schedule"), [navigate]);

	return (
		<Box sx={{ width: "100%", overflow: "hidden", minWidth: 0, bgcolor: planner.canvas }}>
			<Box ref={boxRef} sx={{ flexGrow: 1, position: "relative" }}>
				<Box
					sx={{
						px: 0,
						pb: 0,
						height: `calc(${schedulerContainerHeight}px - 24px)`,
						overflow: "auto",
					}}
				>
					<LoadBalanceBoard
						weekAssignments={roster.plantAssignments}
						allAssignments={roster.allAssignments}
						manufacturingOrders={roster.manufacturingOrders}
						weekStartDate={weekStartDate}
						selectedDate={selectedDate}
						onDateChange={handleDayDateChange}
						locationName={roster.locationName}
						requiredHours={roster.dayRequiredHours}
						onAllocate={handleAllocate}
						onAllocateWeek={handleAllocateWeek}
						allocating={allocation.allocating}
						operationNames={roster.boardOperationNames}
						laborStandards={roster.laborStandards}
						shifts={roster.shifts}
						availability={roster.availability}
						onOpenFlexScheduler={openFlexScheduler}
						onPublishDrafts={publish.publishDrafts}
						locationId={roster.selectedLocation}
						workcenters={roster.plantWorkcenterNames}
						jobs={roster.knownJobs}
						onAssignmentsChanged={roster.refetchAssignments}
						onPlanChanged={roster.fetchWeek}
						invalidateTargetsRef={invalidateTargetsRef}
						layout={layout}
					/>
				</Box>
			</Box>

			<StagedAllocationDialog
				open={allocation.dialogOpen}
				dayLabel={allocationSpan.label}
				orderCount={allocationSpan.orderCount}
				result={allocation.result}
				error={allocation.error}
				onClose={allocation.closeDialog}
			/>

			<RosterSnackbar state={publish.snackbar} onClose={publish.closeSnackbar} />
		</Box>
	);
};

export default Roster;
